/**
 * What each chat type actually needs.
 *
 * `open()` is not one sequence. Only live chat has a running conversation:
 * in the Stencil widget both the socket connection and the history backfill
 * (`getPreviousChatMessages`) are reached solely from liveChatPane, and the
 * other types never touch either. An email/SMS widget collects details and
 * the business follows up out of band; WhatsApp, Facebook and Instagram hand
 * off to the relevant app; a voice widget opens a call.
 *
 * Doing the live-chat sequence for all of them would open a socket nothing
 * listens to and request history that does not exist, on every page load, for
 * the majority of widgets.
 *
 * Two of these types are not fixed. `both` and `allInOneChat` let the visitor
 * choose, and the widget tracks that separately from the configured type
 * (`chatTypeState` vs the `chatType` prop). So capabilities are resolved from
 * the *active* type, which can change after `open()` — see `setActiveChatType`
 * on the client.
 */

import type { ChatType } from '../../types';

export interface ChatCapabilities {
  /** A live message thread: socket, history, unread, typing. */
  conversation: boolean;
  /** Collects visitor details before or instead of chatting. */
  contactForm: boolean;
  /** Opens a voice call. */
  voice: boolean;
  /** Hands the visitor off to an external app rather than chatting here. */
  handoff: boolean;
}

const CAPABILITIES: Record<ChatType, ChatCapabilities> = {
  liveChat: { conversation: true, contactForm: true, voice: false, handoff: false },
  emailChat: { conversation: false, contactForm: true, voice: false, handoff: false },
  voiceAiChat: { conversation: false, contactForm: true, voice: true, handoff: false },
  waChat: { conversation: false, contactForm: true, voice: false, handoff: true },
  facebookChat: { conversation: false, contactForm: false, voice: false, handoff: true },
  instagramChat: { conversation: false, contactForm: false, voice: false, handoff: true },

  // The visitor picks. Until they do, assume the richest set so nothing is
  // unavailable at the moment they choose it.
  both: { conversation: true, contactForm: true, voice: false, handoff: false },
  allInOneChat: { conversation: true, contactForm: true, voice: true, handoff: true },
};

/** Types whose real behaviour is only known once the visitor chooses. */
const SELECTABLE: ReadonlySet<ChatType> = new Set<ChatType>(['both', 'allInOneChat']);

export function capabilitiesFor(chatType: ChatType): ChatCapabilities {
  return CAPABILITIES[chatType] || CAPABILITIES.liveChat;
}

export function isSelectable(chatType: ChatType): boolean {
  return SELECTABLE.has(chatType);
}
